import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Grid,
  Tooltip,
} from "@mui/material";
import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { ISingleProduct, truncateStyle } from "./constants";
import { IconProduct } from "../../../../utils/icons";
import { PdpCountPill, PlacedPill } from "../../../../components/PdpCountPill/PdpCountPill";
import { ProductMenu } from "./ProductMenu";
import { isInternal } from "../../../../utils";
import { useUser } from "../../../../hooks/useUser";
import IdPill from "../../../../components/IdPill/IdPill";

interface Props {
  product: ISingleProduct;
  onUpdate?: () => void;
}

export function SingleProduct({ product, ...rest }: Props) {
  const { role } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [hoverState, setHoverState] = useState<boolean>(false);
  const [storiesCount, setStoriesCount] = useState<any>(0);

  const openProductDetail = () => {
    searchParams.set("productId", product.desireid.toString());
    setSearchParams(searchParams);
  };

  const videosCountText = useMemo(() => {
    const videosCount = product.productVideos.length;
    if (videosCount === 1) return `${videosCount} video`;
    return `${videosCount} videos`;
  }, [product]);

  const countStories = (product: any) => {
    let total = 0;
    let stories: any = [];
    for (let i = 0; i < product.productVideos.length; i++) {
      if (product.productVideos[i].embeds) {
        stories = stories.concat(product.productVideos[i].embeds);
      }
    }
    stories = [
      ...new Map(stories.map((item: any) => [item["id"], item])).values(),
    ];
    total = stories.length;
    return total;
  };

  const storiesCountText = useMemo(() => {
    if (storiesCount === 1) return `${storiesCount} story`;
    return `${storiesCount} stories`;
  }, [product]);

  const pdpCount = product.pdp_url_count || 0;

  useEffect(() => {
    setStoriesCount(countStories(product));
  }, []);

  return (
    <Grid item xs={1}>
      <Card
        elevation={hoverState ? 10 : 0}
        onMouseOver={() => setHoverState(true)}
        onMouseLeave={() => setHoverState(false)}
        className="border border-border-token w-full cursor-pointer"
        onClick={openProductDetail}
      >
        <div className="px-4 py-3 flex flex-col align-start gap-3">
          <Box className="flex justify-between align-center gap-2">
            <Tooltip title={product.title}>
              <Typography
                className="!text-md text-text-1 hover:text-accent-text hover:underline product-title-bold"
                onClick={openProductDetail}
                sx={{
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                  fontFamily: "'CircularStd', Helvetica, Arial, sans-serif",
                  letterSpacing: '-0.015em',
                }}
              >
                {product.title}
              </Typography>
            </Tooltip>
            <Box
              onClick={(e) => e.stopPropagation()}
              className="items-center flex"
            >
              {isInternal(role) && <IdPill value={product.desireid} />}

              <ProductMenu
                {...rest}
                product={product}
                onClose={() => setHoverState(false)}
              />
            </Box>
          </Box>
          <Box className="relative">
            {product.image ? (
              // Product photos ship with white baked into their pixels, so the
              // tile is white in BOTH themes — without it the photo reads as a
              // sharp-cornered white rectangle floating on the card, since
              // objectFit:contain letterboxes the image INSIDE the element and
              // the element's radius never touches the image's own corners.
              // Radius matches the no-image placeholder below.
              <Box className="flex h-[155px] w-full items-center justify-center bg-white rounded-xl overflow-hidden">
                <CardMedia
                  component="img"
                  style={{ height: 155, objectFit: "contain" }}
                  image={product.image}
                  alt=""
                  className="w-full"
                />
              </Box>
            ) : (
              <Box className="flex h-[155px] items-center justify-center bg-tint rounded-xl">
                <IconProduct />
              </Box>
            )}
            <Box className="absolute bottom-2 right-2 flex gap-3">
              {product.productVideos.length > 0 && (
                <Typography
                  className="bg-surface rounded-lg px-2"
                  color="text.secondary"
                >
                  {videosCountText}
                </Typography>
              )}
              {storiesCount > 0 && (
                <Typography
                  className="bg-surface rounded-lg px-2"
                  color="text.secondary"
                >
                  {storiesCountText}
                </Typography>
              )}
              <PdpCountPill count={pdpCount} />
              <PlacedPill count={product.placed_count} />
            </Box>
          </Box>
          <CardContent className="!p-0">
            <Typography
              className="font-normal !text-sm"
              sx={truncateStyle(1)}
              color="text.secondary"
            >
              {product.short_description}
            </Typography>
            {(() => {
              const cat = product.product_categories?.[0];
              if (!cat || cat.name === "Uncategorized") return null;
              return (
                <Box className="mt-1">
                  <Tooltip title={cat.name}>
                    <span className="inline-block bg-surface-2 text-text-2 text-xs font-medium rounded-md px-2 py-[2px] max-w-full truncate">
                      {cat.name}
                    </span>
                  </Tooltip>
                </Box>
              );
            })()}
          </CardContent>
        </div>
      </Card>
    </Grid>
  );
}
